import React, { useState } from 'react';

import { useTranslation } from 'react-i18next';

import { IconCheck } from '@pkg/assets/icons';
import { ReactComponent as QuestionMarkIcon } from '@pkg/assets/icons/v2/question-mark-circle.svg';
import TooltipLarge from '@pkg/components/Elements/TooltipLarge/TooltipLarge';

import { copyToClipboard } from '@app/helpers';

import './_LinkItemButtons.scss';

export interface LinkItemProps {
    link: string;
    buttonText: string;
    tooltipData: Record<string, string> | undefined;
    icon: React.ReactNode;
    index: number;
    onSetShowDropDown: (show: boolean) => void;
    onSetUserInteraction: (userInteraction: boolean) => void;
    directionUp?: boolean;
}

export default function LinkItemButtons({
    onSetShowDropDown,
    tooltipData,
    link,
    buttonText,
    icon,
    onSetUserInteraction,
    index,
    directionUp,
}: LinkItemProps) {
    const { t } = useTranslation(['copy_widget', 'common']);
    const [buttonClickedIdx, setButtonClickedIdx] = useState<number | null>(null);
    const [dropdownClosed, setDropdownClosed] = useState(false);
    function closeDropdown() {
        setTimeout(function () {
            setButtonClickedIdx(null);
            onSetShowDropDown(false);
            onSetUserInteraction(false);
        }, 1000);
        setDropdownClosed(true);
    }
    function onCopyClick(link: string, buttonIdx: number | null): void {
        setButtonClickedIdx(buttonIdx);
        copyToClipboard(link, closeDropdown);
    }

    const handleClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        onCopyClick(link, index);
    };

    const clicked = buttonClickedIdx === index;

    const ctaText = clicked ? t('link_copied') : buttonText;

    return (
        <div
            data-testid="link-button"
            data-link={link}
            className={`copy-module__link-item ${clicked && 'clicked'}`}
            onClick={handleClick}
        >
            <div className="left-content">
                {clicked ? <IconCheck /> : icon}
                <p className="copy-module__link-item__text">{ctaText}</p>
            </div>

            {tooltipData && Object.keys(tooltipData).length > 0 && (
                <TooltipLarge
                    dropdownClosed={dropdownClosed}
                    header={tooltipData.header}
                    bodyCopy={tooltipData.body}
                    directionUp={directionUp}
                >
                    <QuestionMarkIcon
                        className="copy-module__link-item__tool-tip-icon"
                        aria-label={t('alt_copy_link_icon')}
                    />
                </TooltipLarge>
            )}
        </div>
    );
}
